import { useState } from "react"
import { PencilIcon } from "@heroicons/react/24/outline"
import { Button } from "@/components/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { Input } from "@/components/Form/Input"
import { SwitchField } from "@/components/Form/Switch"
import { useToast } from "@/components/Toast"
import { useUpdateAffiliate, type Affiliate } from "@/hooks/useAffiliates"
import { APIError } from "@/api"
import {
  dateInputToUtcIso,
  formatDateUtc,
  isoToDateInput,
  parseRecipients,
} from "@/lib/affiliateStatements"

const MODEL_LABEL: Record<Affiliate["commission_model"], string> = {
  first_payment: "Percent of the first payment",
  first_year_net: "Percent of net revenue in the first year",
}

const CADENCE_LABEL: Record<Affiliate["statement_cadence"], string> = {
  none: "No statements",
  monthly: "Monthly",
  quarterly: "Quarterly",
}

const SELECT_CLASS =
  "w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"

type TermsAffiliate = Pick<
  Affiliate,
  | "commission_model"
  | "commission_window_months"
  | "program_starts_at"
  | "program_ends_at"
  | "statement_cadence"
  | "statement_auto_send"
  | "statement_recipients"
  | "contact_email"
>

function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-secondary">{label}</div>
      <div className="text-sm text-primary">{children}</div>
    </div>
  )
}

/** The affiliate's commission model, program window and statement settings. */
export function AffiliateProgramTerms({
  publicId,
  affiliate,
  hasReferrals,
}: {
  publicId: string
  affiliate: TermsAffiliate
  // Once true the commission model, and a first-year-net window, are locked (the server enforces it).
  hasReferrals: boolean
}) {
  const { toast } = useToast()
  const updateAffiliate = useUpdateAffiliate()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    commissionModel: "first_payment" as Affiliate["commission_model"],
    windowMonths: "12",
    startsAt: "",
    endsAt: "",
    cadence: "none" as Affiliate["statement_cadence"],
    autoSend: false,
    recipients: "",
  })

  const isNet = affiliate.commission_model === "first_year_net"
  const windowLocked = hasReferrals && isNet
  const recipients = parseRecipients(form.recipients)
  const windowMonths = Number(form.windowMonths)
  const windowValid = Number.isInteger(windowMonths) && windowMonths >= 1 && windowMonths <= 60
  const datesValid = !form.startsAt || !form.endsAt || form.endsAt > form.startsAt
  const recipientsValid = recipients.invalid.length === 0 && recipients.emails.length <= 20
  const canSave = windowValid && datesValid && recipientsValid

  const openDialog = () => {
    setForm({
      commissionModel: affiliate.commission_model,
      windowMonths: String(affiliate.commission_window_months),
      startsAt: isoToDateInput(affiliate.program_starts_at),
      endsAt: isoToDateInput(affiliate.program_ends_at),
      cadence: affiliate.statement_cadence,
      autoSend: affiliate.statement_auto_send,
      recipients: (affiliate.statement_recipients ?? []).join("\n"),
    })
    setOpen(true)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSave) return
    // Dates are sent only when changed, so an untouched start keeps its stored instant even
    // if it was set with a time of day.
    const startsChanged = form.startsAt !== isoToDateInput(affiliate.program_starts_at)
    const endsChanged = form.endsAt !== isoToDateInput(affiliate.program_ends_at)
    try {
      await updateAffiliate.mutateAsync({
        publicId,
        commissionModel: form.commissionModel,
        commissionWindowMonths: windowMonths,
        ...(startsChanged ? { programStartsAt: dateInputToUtcIso(form.startsAt) } : {}),
        ...(endsChanged ? { programEndsAt: dateInputToUtcIso(form.endsAt) } : {}),
        statementCadence: form.cadence,
        statementAutoSend: form.autoSend,
        statementRecipients: recipients.emails.length > 0 ? recipients.emails : null,
      })
      toast({ title: "Program terms updated" })
      setOpen(false)
    } catch (err) {
      toast({
        title: "Error updating program terms",
        description: err instanceof APIError ? err.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  return (
    <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-primary">Program terms</h2>
        <Button variant="outline" size="sm" onClick={openDialog}>
          <PencilIcon className="h-4 w-4" />
          Edit terms
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Term label="Commission model">{MODEL_LABEL[affiliate.commission_model]}</Term>
        <Term label="Commission window">
          {isNet ? `${affiliate.commission_window_months} months` : "First payment only"}
        </Term>
        <Term label="Program dates (UTC)">
          {affiliate.program_starts_at ? formatDateUtc(affiliate.program_starts_at) : "No start"}
          {" to "}
          {affiliate.program_ends_at ? formatDateUtc(affiliate.program_ends_at) : "no end"}
        </Term>
        <Term label="Statements">{CADENCE_LABEL[affiliate.statement_cadence]}</Term>
        <Term label="Auto-send">{affiliate.statement_auto_send ? "On" : "Off"}</Term>
        <Term label="Statement recipients">
          {affiliate.statement_recipients?.length ? (
            affiliate.statement_recipients.join(", ")
          ) : (
            <span className="text-secondary">{affiliate.contact_email} (contact email)</span>
          )}
        </Term>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit program terms</DialogTitle>
            <DialogDescription>
              Statements already sent keep the rate and amounts they were issued with. Changes
              apply to statements generated from now on.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Commission model</label>
              <select
                value={form.commissionModel}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    commissionModel: e.target.value as Affiliate["commission_model"],
                  }))
                }
                disabled={hasReferrals}
                className={SELECT_CLASS}
              >
                <option value="first_payment">{MODEL_LABEL.first_payment}</option>
                <option value="first_year_net">{MODEL_LABEL.first_year_net}</option>
              </select>
              {hasReferrals && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Locked: someone has already signed up through this affiliate. Create a new
                  affiliate for new terms.
                </p>
              )}
            </div>
            <Input
              name="windowMonths"
                label="Commission window (months)"
              type="number"
              min={1}
              max={60}
              value={form.windowMonths}
              onChange={(e) => setForm((s) => ({ ...s, windowMonths: e.target.value }))}
              disabled={windowLocked}
              error={windowValid ? undefined : "Enter a whole number from 1 to 60"}
              helperText={
                windowLocked
                  ? "Locked: someone has already signed up through this affiliate."
                  : "How long after a team's first payment commission keeps accruing."
              }
              required
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                name="startsAt"
                label="Program starts (UTC)"
                type="date"
                value={form.startsAt}
                onChange={(e) => setForm((s) => ({ ...s, startsAt: e.target.value }))}
              />
              <Input
                name="endsAt"
                label="Program ends (UTC)"
                type="date"
                value={form.endsAt}
                onChange={(e) => setForm((s) => ({ ...s, endsAt: e.target.value }))}
                error={datesValid ? undefined : "Must be after the start"}
              />
            </div>
            {hasReferrals && (
              <p className="-mt-2 text-sm text-muted-foreground">
                Dates can only be widened now that someone has signed up.
              </p>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">Statement cadence</label>
              <select
                value={form.cadence}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    cadence: e.target.value as Affiliate["statement_cadence"],
                  }))
                }
                className={SELECT_CLASS}
              >
                <option value="none">{CADENCE_LABEL.none}</option>
                <option value="monthly">{CADENCE_LABEL.monthly}</option>
                <option value="quarterly">{CADENCE_LABEL.quarterly}</option>
              </select>
            </div>
            <SwitchField
              label="Send statements automatically"
              description="When off, the daily job builds each draft and you send it from the statement page."
              checked={form.autoSend}
              onCheckedChange={(checked) => setForm((s) => ({ ...s, autoSend: checked }))}
            />
            <div className="space-y-2">
              <label htmlFor="statement-recipients" className="text-sm font-medium leading-none">
                Statement recipients
              </label>
              <textarea
                id="statement-recipients"
                rows={3}
                value={form.recipients}
                onChange={(e) => setForm((s) => ({ ...s, recipients: e.target.value }))}
                placeholder={affiliate.contact_email}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              />
              <p
                className={`text-sm ${recipientsValid ? "text-muted-foreground" : "text-destructive"}`}
              >
                {recipients.invalid.length > 0
                  ? `Not an email address: ${recipients.invalid.join(", ")}`
                  : recipients.emails.length > 20
                    ? "At most 20 recipients"
                    : "One per line. Leave empty to use the contact email."}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateAffiliate.isPending || !canSave}>
                {updateAffiliate.isPending ? "Saving..." : "Save terms"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
